import { DiplomaIdType, SESSION_STORAGE_UUID_KEY } from '@/data/constants'
import {
  Course,
  ExtraPoints,
  nullExtraPoints,
  nullUserDetails,
  UserDetails,
} from '@/data/models'
import { normalizeExtraPointsForStudyStartYear } from '@/calculator/calculator'
import type { ReportCardImportedCourse } from '@/lib/reportCardImport'
import { create } from 'zustand'
import coursesList from '../data/coursesList'
import { generate } from 'short-uuid'

export interface CalculatorStore {
  status?: 'new' | 'modified' | 'saved'
  userDetails: UserDetails
  extraPoints: ExtraPoints
  courses: Course[]
  inputDiploma: DiplomaIdType
  improvedDiploma: DiplomaIdType
  dreamStudy: string
  uuid: string
  show235: boolean
  hasUsedAiAnalyzer: boolean
  setData: (newData: Partial<CalculatorStore>) => void
  setStatus: (status: 'new' | 'modified' | 'saved') => void
  setHasUsedAiAnalyzer: (hasUsedAiAnalyzer: boolean) => void
  setUuid: () => void
  setBirthYear: (birthYear: number) => void
  setExtraPoints: (extraPoints: ExtraPoints) => void
  setStudyStartYear: (studyStartYear: number) => void
  setEmail: (email: string) => void
  setDreamStudy: (dreamStudy: string) => void
  setDiploma: (diploma: DiplomaIdType, type: 'improved' | 'input') => void
  addNewCourse: (course: Course) => void
  addCourse: (courseId: string) => void
  applyImportedCourses: (imports: ReportCardImportedCourse[]) => void
  removeCourse: (courseId: string) => void
  changeGrade: (
    courseId: string,
    grade: number,
    type: 'grade' | 'exam' | 'exam2' | 'improved' | 'improved2'
  ) => void
  increaseNumExams: (courseId: string) => void
  decreaseNumExams: (courseId: string) => void
  setShow235: (show235: boolean) => void
  toggleCourseVisibility: (courseId: string) => void
}

export const useCalculatorStore = create<CalculatorStore>((set) => ({
  status: 'new',
  userDetails: nullUserDetails,
  extraPoints: nullExtraPoints,
  courses: coursesList,
  inputDiploma: 'SSREAL',
  improvedDiploma: 'SSREAL',
  dreamStudy: '',
  uuid: '',
  show235: false,
  hasUsedAiAnalyzer: false,

  setData: (newData: Partial<CalculatorStore>) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, ...newData }))
  },

  setStatus: (status: 'new' | 'modified' | 'saved') => {
    set(() => ({ status }))
  },

  setHasUsedAiAnalyzer: (hasUsedAiAnalyzer: boolean) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, hasUsedAiAnalyzer }))
  },

  setUuid: () => {
    set((state) => {
      if (state.uuid !== '') {
        return state
      }

      try {
        const storedUuid = sessionStorage.getItem(SESSION_STORAGE_UUID_KEY)
        if (storedUuid) {
          return { uuid: storedUuid }
        }
      } catch (error) {
        console.warn('Failed to read from sessionStorage:', error)
      }

      const newUuid = generate()
      try {
        sessionStorage.setItem(SESSION_STORAGE_UUID_KEY, newUuid)
      } catch (error) {
        console.warn('Failed to write to sessionStorage:', error)
      }
      return { uuid: newUuid }
    })
  },

  setBirthYear: (birthyear: number) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      userDetails: { ...state.userDetails, birthyear },
    }))
  },

  setExtraPoints: (extraPoints: ExtraPoints) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      extraPoints: normalizeExtraPointsForStudyStartYear(
        extraPoints,
        state.userDetails.studyStartYear
      ),
    }))
  },

  setStudyStartYear: (studyStartYear: number) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      userDetails: { ...state.userDetails, studyStartYear },
      extraPoints: normalizeExtraPointsForStudyStartYear(
        state.extraPoints,
        studyStartYear
      ),
    }))
  },

  setEmail: (email: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, userDetails: { ...state.userDetails, email } }))
  },

  setDreamStudy: (dreamStudy: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, dreamStudy }))
  },

  setDiploma: (diploma: DiplomaIdType, type: 'improved' | 'input') => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      ...(type === 'input'
        ? { improvedDiploma: diploma, inputDiploma: diploma }
        : { improvedDiploma: diploma }),
    }))
  },

  addNewCourse: (course: Course) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, courses: [...state.courses, course] }))
  },

  addCourse: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      courses: state.courses.map((c) =>
        c.id === courseId
          ? {
              ...c,
              included: true,
            }
          : c
      ),
    }))
  },

  applyImportedCourses: (imports: ReportCardImportedCourse[]) => {
    const importsByCourseId = new Map<string, ReportCardImportedCourse>()

    for (const item of imports) {
      const existing = importsByCourseId.get(item.courseId)
      if (!existing) {
        importsByCourseId.set(item.courseId, item)
        continue
      }

      importsByCourseId.set(item.courseId, {
        courseId: item.courseId,
        grade: existing.grade || item.grade,
        exam: existing.exam || item.exam,
        exam2: existing.exam2 || item.exam2,
      })
    }

    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      hasUsedAiAnalyzer: true,
      courses: state.courses.map((course) => {
        const importedCourse = importsByCourseId.get(course.id)
        if (!importedCourse) {
          return {
            ...course,
            included: false,
            grade: 0,
            exam: 0,
            exam2: 0,
            improved: 0,
            improved2: 0,
            numExams: 0,
            hasTwoExams: course.hasTwoExams,
          }
        }

        const normalizedExam =
          importedCourse.exam > 0
            ? importedCourse.exam
            : importedCourse.exam2 > 0
              ? importedCourse.exam2
              : 0
        const normalizedExam2 =
          importedCourse.exam > 0 && importedCourse.exam2 > 0
            ? importedCourse.exam2
            : 0
        const numExams =
          (normalizedExam > 0 ? 1 : 0) + (normalizedExam2 > 0 ? 1 : 0)

        return {
          ...course,
          included: true,
          grade: importedCourse.grade,
          exam: normalizedExam,
          exam2: normalizedExam2,
          improved: 0,
          improved2: 0,
          numExams: numExams as 0 | 1 | 2,
          hasTwoExams: course.hasTwoExams || numExams === 2,
          visible: true,
        }
      }),
    }))
  },

  removeCourse: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? {
              ...c,
              included: false,
            }
          : c
      ),
    }))
  },

  changeGrade: (
    courseId: string,
    grade: number,
    type: 'grade' | 'exam' | 'exam2' | 'improved' | 'improved2'
  ) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId ? { ...c, [type]: grade } : c
      ),
    }))
  },

  increaseNumExams: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? { ...c, numExams: (c.numExams + 1) as 0 | 1 | 2 }
          : c
      ),
    }))
  },

  decreaseNumExams: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? { ...c, numExams: (c.numExams - 1) as 0 | 1 | 2 }
          : c
      ),
    }))
  },

  setShow235: (show235: boolean) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, show235 }))
  },

  toggleCourseVisibility: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId ? { ...c, visible: c.visible === false } : c
      ),
    }))
  },
}))
